import React, { useState, useCallback } from "react";
import useBinder, { GRIDS } from "./useBinder";
import SlotPicker from "./SlotPicker";
import BinderFiles from "./BinderFiles";

/**
 * Lay a binder out before you commit sleeves to it.
 *
 * Two facing pages at a time, because that is what you see when a binder is
 * open and it is the unit a page layout actually reads in — a spread that looks
 * right one page at a time can still look wrong once the book is open.
 *
 * Cards are moved by tapping one and then tapping where it should go. Dragging
 * is nicer with a mouse and unusable with a thumb, and this is a thing people
 * will do at a table with a phone in one hand.
 */

function Slot({ index, card, moving, over, onOpen, onStartMove, onMove, onClear, onDragTo, onOver }) {
  const isMoving = moving === index;

  /* Three meanings for one tap, decided by what is going on rather than by a
     mode you have to select: with something in hand, every slot is a
     destination; otherwise an empty slot wants filling and a card wants
     picking up. */
  const act = () => {
    if (moving != null) onMove(index);
    else if (card) onStartMove(index);
    else onOpen(index);
  };

  const label = moving != null
    ? `Move here${card ? `, swapping with ${card.name}` : ""}`
    : card
      ? `Pick up ${card.name}${card.number ? `, ${card.number}` : ""}`
      : `Fill slot ${index + 1}`;

  /* Dragging as well as tapping, not instead of it. A drag is the obvious
     gesture with a mouse; it is also the one that does nothing on a touch
     screen, where the browser owns that gesture for scrolling. Tapping stays
     the path that works everywhere. */
  const dragProps = {
    draggable: Boolean(card),
    onDragStart: (e) => {
      e.dataTransfer.setData("text/plain", String(index));
      e.dataTransfer.effectAllowed = "move";
      onStartMove(index);
    },
    onDragEnd: () => { onStartMove(null); onOver(null); },
    onDragOver: (e) => {
      if (moving == null) return;
      e.preventDefault();                 // without this the drop never fires
      e.dataTransfer.dropEffect = "move";
      if (over !== index) onOver(index);
    },
    onDragLeave: () => { if (over === index) onOver(null); },
    onDrop: (e) => {
      e.preventDefault();
      const from = Number(e.dataTransfer.getData("text/plain"));
      onOver(null);
      if (Number.isFinite(from)) onDragTo(from, index);
    },
  };

  return (
    <div
      className={
        `bnd-slot${card ? " is-filled" : ""}${isMoving ? " is-moving" : ""}` +
        `${over === index ? " is-over" : ""}`
      }
    >
      <button type="button" className="bnd-slot-hit" onClick={act} aria-label={label} {...dragProps}>
        {card?.image ? (
          /* Not lazy. A slot is absolutely positioned inside an aspect-ratio
             box, and in that arrangement the browser never decides the image
             is on screen — currentSrc stays empty and the fetch never starts,
             even after scrolling it into the middle of the viewport. A spread
             is at most 32 cards, so there is nothing to defer anyway. */
          <img src={card.image} alt="" />
        ) : card ? (
          <span className="bnd-slot-name">{card.name}</span>
        ) : (
          <span className="bnd-slot-plus" aria-hidden="true">+</span>
        )}
      </button>
      {card && moving == null && (
        <button
          type="button"
          className="bnd-slot-x"
          onClick={() => onClear(index)}
          aria-label={`Take ${card.name} out`}
        >
          ×
        </button>
      )}
    </div>
  );
}

function Page({ start, count, cols, slots, ...rest }) {
  return (
    <div
      className="bnd-page"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Slot key={start + i} index={start + i} card={slots[start + i]} {...rest} />
      ))}
    </div>
  );
}

export default function Binder() {
  const {
    binder, perPage, capacity, filled, overflow, value,
    place, clear, swap, setGrid, setPages, rename, replace,
  } = useBinder();

  // Which spread is open. A spread is two facing pages.
  const [spread, setSpread] = useState(0);
  const [picking, setPicking] = useState(null);   // slot awaiting a card
  const [moving, setMoving] = useState(null);     // slot picked up to be moved
  const [over, setOver] = useState(null);         // slot a drag is hovering

  const spreads = Math.ceil(binder.pages / 2);
  const at = Math.min(spread, spreads - 1);
  const leftPage = at * 2;
  const rightPage = leftPage + 1;

  const openSlot = useCallback((i) => {
    setMoving(null);
    setPicking(i);
  }, []);

  // Tapping the card already in hand puts it back down, rather than trapping
  // you in a move you have changed your mind about.
  const onMove = useCallback(
    (to) => {
      if (to !== moving) swap(moving, to);
      setMoving(null);
    },
    [moving, swap]
  );

  const pick = useCallback(
    (i, card) => {
      place(i, card);
      setPicking(null);
    },
    [place]
  );

  // A drop names both ends itself, so it never depends on what was in hand.
  const dragTo = useCallback(
    (from, to) => {
      setMoving(null);
      if (Number.isFinite(from) && from !== to) swap(from, to);
    },
    [swap]
  );

  const slotProps = {
    moving, over, onOpen: openSlot, onStartMove: setMoving, onMove,
    onClear: clear, onDragTo: dragTo, onOver: setOver,
  };

  return (
    <div className="bnd">
      <div className="bnd-head">
        <h2 className="section-title">Binder</h2>
        <p className="bnd-sub">
          Lay a page out and see how it looks before you sleeve it. Saved on this
          device, and to your account if you are signed in.
        </p>
      </div>

      <div className="bnd-bar">
        <input
          className="bnd-name"
          value={binder.name}
          onChange={(e) => rename(e.target.value)}
          aria-label="Binder name"
        />

        <div className="bnd-grids" role="group" aria-label="Page size">
          {GRIDS.map((g) => (
            <button
              key={g.label}
              type="button"
              className={`bnd-grid-btn${binder.cols === g.cols && binder.rows === g.rows ? " is-on" : ""}`}
              onClick={() => setGrid(g.cols, g.rows)}
            >
              {g.label}
            </button>
          ))}
        </div>

        <label className="bnd-pages">
          Pages
          <button type="button" onClick={() => setPages(binder.pages - 1)} aria-label="One page fewer">−</button>
          <span>{binder.pages}</span>
          <button type="button" onClick={() => setPages(binder.pages + 1)} aria-label="One page more">+</button>
        </label>
      </div>

      {/* Named rather than left to be discovered when a card is missing. */}
      {overflow > 0 && (
        <p className="msg-empty">
          {overflow} card{overflow === 1 ? " sits" : "s sit"} past the last page.
          {overflow === 1 ? " It is" : " They are"} kept, not thrown away — add pages,
          or use a bigger page, to see {overflow === 1 ? "it" : "them"} again.
        </p>
      )}

      <div className="bnd-spread">
        <button
          type="button"
          className="bnd-turn"
          onClick={() => setSpread(Math.max(0, at - 1))}
          disabled={at === 0}
          aria-label="Previous pages"
        >
          ‹
        </button>

        <div className="bnd-book">
          <Page
            start={leftPage * perPage}
            count={perPage}
            cols={binder.cols}
            slots={binder.slots}
            {...slotProps}
          />
          {rightPage < binder.pages ? (
            <Page
              start={rightPage * perPage}
              count={perPage}
              cols={binder.cols}
              slots={binder.slots}
              {...slotProps}
            />
          ) : (
            <div className="bnd-page bnd-page--none" aria-hidden="true" />
          )}
        </div>

        <button
          type="button"
          className="bnd-turn"
          onClick={() => setSpread(Math.min(spreads - 1, at + 1))}
          disabled={at >= spreads - 1}
          aria-label="Next pages"
        >
          ›
        </button>
      </div>

      <div className="bnd-foot">
        <span className="bnd-where">
          Page{rightPage < binder.pages ? `s ${leftPage + 1}–${rightPage + 1}` : ` ${leftPage + 1}`} of {binder.pages}
        </span>
        <span className="bnd-count">
          {filled} of {capacity} slots filled
        </span>
        <span className="bnd-value">
          {value > 0 ? `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"}
        </span>
      </div>

      {moving != null ? (
        <p className="bnd-hint">
          Tap where it should go. <button type="button" onClick={() => setMoving(null)}>Cancel</button>
        </p>
      ) : (
        <p className="bnd-hint bnd-hint--dim">
          Tap a slot to fill it. Drag a card where you want it — or tap it, then tap
          its new home.
        </p>
      )}

      <BinderFiles
        binder={binder}
        onReplace={(next) => { replace(next); setSpread(0); setMoving(null); }}
      />

      {picking != null && (
        <SlotPicker
          slot={picking}
          onPick={pick}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}
